import { isUS } from '~/modules/root/page/us-footer-gating';

/**
 * Market wording for the privacy opt-out, shared by `/your-privacy-choices`, its email
 * form and the account privacy settings so the three surfaces describe the same control
 * the same way.
 *
 * The control is identical in every market (see `shouldAllowThirdPartySharing`); only the
 * framing differs. The US frames it with US state privacy law ("sale or sharing",
 * sensitive personal information, non-discrimination). NZ and AU have none of those
 * rights, so there it is an opt-out of targeted advertising. The wording claims no more
 * than the control does: it does not switch off every analytics or marketing tool, so it
 * speaks of advertising partners only.
 *
 * Global Privacy Control wording is not here: whether a market honours GPC is
 * `countryConfig.honourGlobalPrivacyControl`, and callers show the GPC sentences from that.
 */
export interface PrivacyChoicesCopy {
    /** First sentence of the page, before the link to the Privacy Policy. */
    pageIntro: string;
    /** Search-result description for the page. */
    metaDescription: string;
    /** Heading of the opt-out section, and the label of the settings toggle. */
    optOutTitle: string;
    /** What opting out covers. */
    optOutSummary: string;
    /** What stops once a browser is opted out, as in "We have stopped ___ for this browser." */
    stoppedActivity: string;
    /** Whether the US-only rights (sensitive personal information, non-discrimination) apply. */
    hasUsStateRights: boolean;
}

const US_COPY: PrivacyChoicesCopy = {
    pageIntro:
        'This page lets US residents exercise their choices about how ArchiPro uses their personal information.',
    metaDescription:
        'Opt out of the sale or sharing of your personal information, limit the use of sensitive personal information, and see how ArchiPro honours Global Privacy Control signals.',
    optOutTitle: 'Do not sell or share my personal information',
    optOutSummary:
        'Some of our sharing of personal information with advertising and analytics partners may be a “sale” or “sharing” under US state privacy laws. You can opt out of that sharing for targeted advertising.',
    stoppedActivity: 'selling or sharing personal information',
    hasUsStateRights: true,
};

const DEFAULT_COPY: PrivacyChoicesCopy = {
    pageIntro:
        'This page lets you choose how ArchiPro uses your personal information for advertising.',
    metaDescription:
        'Opt out of ArchiPro using your personal information for targeted advertising and sharing it with advertising partners.',
    optOutTitle: 'Opt out of targeted advertising',
    optOutSummary:
        'You can opt out of our use of your personal information for targeted advertising, including our sharing of it with advertising partners.',
    stoppedActivity: 'sharing personal information with advertising partners',
    hasUsStateRights: false,
};

/**
 * Wording for the given market's privacy opt-out.
 */
export const getPrivacyChoicesCopy = (country: string): PrivacyChoicesCopy =>
    isUS(country) ? US_COPY : DEFAULT_COPY;

/**
 * Sentence shown wherever the opt-out is described, in markets that honour Global
 * Privacy Control.
 */
export const GLOBAL_PRIVACY_CONTROL_SUMMARY =
    'We also honour Global Privacy Control (GPC) signals automatically, so if your browser sends a GPC signal we treat it as an opt-out for that browser or device.';
